import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import TypeaheadSearch, { TypeaheadOption } from '../../../components/ui/TypeaheadSearch'
import Badge from '../../../components/ui/Badge'
import Table, { Pagination } from '../../../components/ui/Table'
import Modal from '../../../components/ui/Modal'
import api from '../../../lib/api'
import { formatCurrency } from '../../../lib/formatters'
import type { CatalogItem } from '../../../types'

const CATEGORIES = [
  'Crowns',
  'Bridges',
  'Dentures',
  'Implants',
  'Appliances',
  'Staining',
  'Orthodontics',
  'Veneers',
  'Other',
]

// SAC codes for dental services (suppressing HSN - using SAC for services)
const SAC_CODES = [
  { code: '999312', description: 'Dental services (0% GST for healthcare)' },
  { code: '998599', description: 'Other professional services' },
]

export default function CatalogListPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null)

  interface WorkItemsResponse {
    data: CatalogItem[]
    meta: {
      page: number
      per_page: number
      total: number
      total_pages: number
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['catalog', page, search, category],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({ page: String(page), per_page: '10' })
        if (search) params.append('search', search)
        if (category) params.append('category', category)
        const { data } = await api.get<WorkItemsResponse>(`/work-items?${params}`)
        return {
          data: data.data || [],
          totalPages: data.meta?.total_pages || 1,
          total: data.meta?.total || 0,
        }
      } catch (err) {
        console.error('Failed to fetch catalog items:', err)
        return { data: [], totalPages: 1, total: 0 }
      }
    },
  })

  const columns = [
    {
      key: 'name',
      header: 'Item Name',
      render: (item: CatalogItem) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{item.name}</p>
          {item.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[300px]">
              {item.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (item: CatalogItem) => (
        <Badge variant="info">{item.category}</Badge>
      ),
    },
    {
      key: 'basePrice',
      header: 'Base Price',
      render: (item: CatalogItem) => (
        <span className="font-medium text-gray-900 dark:text-white">
          {formatCurrency(item.basePrice)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item: CatalogItem) => (
        <Badge variant={item.isActive ? 'success' : 'gray'}>
          {item.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (item: CatalogItem) => (
        <button 
          onClick={(e) => {
            e.stopPropagation()
            setEditingItem(item)
          }}
          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
        >
          <i className="fas fa-edit"></i>
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Price Catalog</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your service items and pricing
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <i className="fas fa-plus mr-2"></i>
          Add Item
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <TypeaheadSearch
              placeholder="Search items by name, category..."
              value={search}
              onChange={(value) => { setSearch(value); setPage(1) }}
              onSelect={(option) => { setSearch(option.label); setPage(1) }}
              queryKey={['catalog', 'search']}
              queryFn={async () => {
                try {
                  const { data } = await api.get<WorkItemsResponse>('/work-items?per_page=100')
                  return (data.data || []).map((item: CatalogItem): TypeaheadOption => ({
                    id: item.id,
                    label: item.name,
                    sublabel: `${item.category} • ${formatCurrency(item.basePrice)}`,
                    icon: 'fa-tag',
                  }))
                } catch {
                  return []
                }
              }}
              maxResults={8}
            />
          </div>
          <SearchableSelect
            value={category}
            onChange={(val) => { setCategory(val); setPage(1) }}
            placeholder="All Categories"
            options={[
              { value: '', label: 'All Categories' },
              ...CATEGORIES.map(cat => ({ value: cat, label: cat })),
            ]}
          />
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={data?.data ?? []}
          loading={isLoading}
          keyExtractor={(item) => item.id}
          emptyMessage="No items found"
        />
        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Create Modal */}
      <CreateCatalogItemModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false)
          queryClient.invalidateQueries({ queryKey: ['catalog'] })
        }}
      />

      {/* Edit Modal */}
      {editingItem && (
        <EditCatalogItemModal
          item={editingItem}
          isOpen={!!editingItem}
          onClose={() => setEditingItem(null)}
          onSuccess={() => {
            setEditingItem(null)
            queryClient.invalidateQueries({ queryKey: ['catalog'] })
          }}
        />
      )}
    </div>
  )
}

interface CreateCatalogItemModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

function CreateCatalogItemModal({ isOpen, onClose, onSuccess }: CreateCatalogItemModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    basePrice: '',
    sacCode: '999312', // Default SAC for dental services
    gstPercent: '0', // Healthcare services are typically 0% GST
  })

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return api.post('/work-items', {
        name: data.name,
        category: data.category,
        description: data.description || undefined,
        basePrice: parseFloat(data.basePrice),
        hsnCode: data.sacCode, // Backend uses hsnCode field for SAC
        gstPercent: parseInt(data.gstPercent),
      })
    },
    onSuccess: () => {
      setFormData({ name: '', category: '', description: '', basePrice: '', sacCode: '999312', gstPercent: '0' })
      onSuccess()
    },
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) newErrors.name = 'Item name is required'
    if (!formData.category) newErrors.category = 'Category is required'
    if (!formData.basePrice || parseFloat(formData.basePrice) < 0) newErrors.basePrice = 'Valid price is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      mutation.mutate(formData)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Catalog Item" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Item Name *"
          value={formData.name}
          onChange={(e) => { setFormData({ ...formData, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: '' }) }}
          error={errors.name}
          placeholder="e.g., Zirconia Crown"
          required
        />

        <SearchableSelect
          label="Category *"
          value={formData.category}
          onChange={(val) => setFormData({ ...formData, category: val })}
          placeholder="Select category"
          options={[
            { value: '', label: 'Select category' },
            ...CATEGORIES.map(cat => ({ value: cat, label: cat })),
          ]}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Base Price (₹)"
            type="number"
            min="0"
            step="0.01"
            value={formData.basePrice}
            onChange={(e) => setFormData({ ...formData, basePrice: e.target.value })}
            required
          />
          <SearchableSelect
            label="SAC Code *"
            value={formData.sacCode}
            onChange={(val) => setFormData({ ...formData, sacCode: val })}
            searchable={false}
            helperText="Service Accounting Code for dental lab services"
            options={SAC_CODES.map(sac => ({
              value: sac.code,
              label: `${sac.code} - ${sac.description}`,
            }))}
          />
        </div>

        <SearchableSelect
          label="GST Percentage *"
          value={formData.gstPercent}
          onChange={(val) => setFormData({ ...formData, gstPercent: val })}
          searchable={false}
          helperText="Dental healthcare services (SAC 999312) typically attract 0% GST"
          options={[
            { value: '0', label: '0% (Healthcare Exempt)' },
            { value: '5', label: '5%' },
            { value: '12', label: '12%' },
            { value: '18', label: '18%' },
            { value: '28', label: '28%' },
          ]}
        />

        {mutation.isError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">
              Failed to create item. Please try again.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            <i className="fas fa-plus mr-2"></i>
            Add Item
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// Edit Catalog Item Modal
interface EditCatalogItemModalProps {
  item: CatalogItem
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

function EditCatalogItemModal({ item, isOpen, onClose, onSuccess }: EditCatalogItemModalProps) {
  const [formData, setFormData] = useState({
    name: item.name,
    category: item.category,
    description: item.description || '',
    basePrice: String(item.basePrice),
    sacCode: item.hsnCode || '999312',
    gstPercent: String(item.gstPercent || 0),
    isActive: item.isActive,
  })

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return api.put(`/work-items/${item.id}`, {
        name: data.name,
        category: data.category,
        description: data.description || null,
        basePrice: parseFloat(data.basePrice),
        hsnCode: data.sacCode,
        gstPercent: parseInt(data.gstPercent),
        isActive: data.isActive,
      })
    },
    onSuccess,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutation.mutate(formData)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Catalog Item" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Item Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />

        <SearchableSelect
          label="Category *"
          value={formData.category}
          onChange={(val) => setFormData({ ...formData, category: val })}
          placeholder="Select category"
          options={[
            { value: '', label: 'Select category' },
            ...CATEGORIES.map(cat => ({ value: cat, label: cat })),
          ]}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Base Price (₹)"
            type="number"
            min="0"
            step="0.01"
            value={formData.basePrice}
            onChange={(e) => setFormData({ ...formData, basePrice: e.target.value })}
            required
          />
          <SearchableSelect
            label="SAC Code *"
            value={formData.sacCode}
            onChange={(val) => setFormData({ ...formData, sacCode: val })}
            searchable={false}
            options={SAC_CODES.map(sac => ({
              value: sac.code,
              label: sac.code,
            }))}
          />
        </div>

        <SearchableSelect
          label="GST Percentage *"
          value={formData.gstPercent}
          onChange={(val) => setFormData({ ...formData, gstPercent: val })}
          searchable={false}
          options={[
            { value: '0', label: '0% (Healthcare Exempt)' },
            { value: '5', label: '5%' },
            { value: '12', label: '12%' },
            { value: '18', label: '18%' },
            { value: '28', label: '28%' },
          ]}
        />

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
          />
          <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
            Item is active and can be used in orders
          </label>
        </div>

        {mutation.isError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">
              Failed to update item. Please try again.
            </p>
          </div>
        )}

        {mutation.isSuccess && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-600 dark:text-green-400">
              <i className="fas fa-check-circle mr-2"></i>
              Item updated successfully!
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            <i className="fas fa-save mr-2"></i>
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  )
}
